import { useAgentContext, useFrontendTool } from "@copilotkit/react-core/v2";
import { z } from "zod";
import App from "app";
import { useLocation } from "react-router";

export const useGetPageContextTool = () => {
    const location = useLocation();

    // HashRouter pathnames start with a leading "/", so split("/") yields an empty
    // first element — drop it before indexing so parts[0] is the actual module.
    const hash = location.pathname;
    const parts = hash.split("/").filter(Boolean);
    const module = parts[0] ?? null;

    const pageContext: Record<string, string | null> = {
        path: hash,
        module
    };

    if (module === "flow") {
        pageContext.appName = parts[1] ?? null;

        const p2 = parts[2];
        const p3 = parts[3];
        const p4 = parts[4];

        if (p2 === "edit") {
            // /flow/{app}/edit/{component} — editing a component at app level, no flow selected
            pageContext.flowName = null;
            pageContext.editingComponent = p3 ?? null;
        } else if (p3 === "edit") {
            // /flow/{app}/{flow}/edit/{component} — editing a component inside a flow
            pageContext.flowName = p2 ?? null;
            pageContext.editingComponent = p4 ?? null;
        } else {
            // /flow/{app}/{flow} — viewing a flow
            pageContext.flowName = p2 ?? null;
            pageContext.editingComponent = null;
        }
    }

    // Expose the page context as readable agent context instead of a tool the agent must
    // call — it stays in sync with the route automatically and is always available.
    useAgentContext({
        description: `The current page context for the user, including the URL hash path and the active module. If the module is "flow", includes appName, flowName, and editingComponent where applicable.`,
        value: pageContext
    });

    useFrontendTool({
        name: "striimPageNavigator",
        description: `Navigates to a particular page in Striim`,
        parameters: z.object({
            path: z.string().describe("The path to navigate. It starts with / and is a string. ")
        }),
        handler: async ({ path }: { path: string }) => {
            try {
                App.navigate(path, { trigger: true });
                return true;
            } catch (e) {
                return { error: e?.message ?? String(e), error_type: "NavigationError" };
            }
        }
    });
};
